import { useEffect } from "react";

export type TStrictAddEventListenerOptions = Omit<AddEventListenerOptions, 'capture'> & { capture: true; };
/**
 * React hook that subscribes to the browser's `freeze` lifecycle event.
 *
 * The `freeze` event is dispatched when the browser is about to freeze the
 * page and suspend JavaScript execution. Use this event to perform any final
 * synchronous work before the page enters a frozen state.
 *
 * For more information about the Page Lifecycle API and the `freeze` event:
 * https://developer.chrome.com/docs/web-platform/page-lifecycle-api
 * https://wicg.github.io/page-lifecycle/spec.html#sec-api
 *
 * @param onFreeze - Callback invoked immediately before the page is frozen.
 * @param options - Optional configuration for the hook. "capture" must be true to
 * reliably capture freeze event due to how lifecycle event propogation works.
 * see https://developer.chrome.com/docs/web-platform/page-lifecycle-api examples.
 *
 * @remarks
 * - Browser support for the `freeze` event is limited.
 * - Execution may be suspended immediately after the event fires.
 * - The callback should complete quickly and avoid relying on asynchronous work.
 */
export function useFreezeLifecycleEvent(
  onFreeze: (event: Event) => void,
  options: TStrictAddEventListenerOptions = { capture: true },
): void {
  useEffect(() => {
    window.addEventListener('freeze', onFreeze, options);
    return () => window.removeEventListener('freeze', onFreeze, options);
  }, [onFreeze, options]);
}